import Vue from 'vue'
import { min } from 'lodash'
import jwtDecode from 'jwt-decode'
import { solveAllErrors } from '../mutations'
import inform from '@/services/inform'

const getUserFromToken = token => {
  if (!token) {
    return {}
  }
  const { payload } = jwtDecode(token)
  return payload.user
}

const getRole = data => {
  let role = -1

  if (!data) {
    return role
  }

  // Is there any role stored in sessionStorage that is still valid?
  let storedRole = sessionStorage.getItem('role')
  if (storedRole) {
    storedRole = parseInt(storedRole, 10)
    if (data.roles && data.roles.includes(storedRole)) {
      role = storedRole
      return storedRole
    }
  }
  // Select highest role per default
  role = min(data.roles)
  if (role) {
    sessionStorage.setItem('role', role)
  }

  return role
}

// State
const defaultState = {
  data: {
    email: null,
    firstName: null,
    lastName: null,
    hasToChangePassword: false,
    roles: [],
    role: -1
  },
  isLoading: false,
  isAnonymous: false,
  isLoggedIn: false,
  isChangingEmail: false,
  isChangingPassword: false
}

const storedUserData = getUserFromToken(sessionStorage.getItem('token'))
const state = {
  ...defaultState,
  data: {
    ...defaultState.data,
    ...storedUserData,
    role: getRole(storedUserData)
  },
  isLoggedIn: !!sessionStorage.getItem('token'),
  isAnonymous: !sessionStorage.getItem('token')
}

// Mutations
export const loginRequest = 'loginRequest'
export const loginSuccess = 'loginSuccess'
export const loginFail = 'loginFail'

export const logoutRequest = 'logoutRequest'
const permissionsRequest = 'permissionsRequest'
const permissionsResponse = 'permissionsResponse'
export const changeRole = 'changeRole'

const changeEmailRequest = 'changeEmailRequest'
const changeEmailResponse = 'changeEmailResponse'
const changePasswordRequest = 'changePasswordRequest'
const changePasswordResponse = 'changePasswordResponse'

const mutations = {
  [loginRequest] (state, { email }) {
    state.data = { email }
    state.isLoading = true
  },
  [loginSuccess] (state, { token }) {
    sessionStorage.setItem('token', token)
    state.data = getUserFromToken(token)
    state.data.role = getRole(state.data)
    state.isAnonymous = false
    state.isLoggedIn = true
    state.isLoading = false
  },
  [loginFail] (state) {
    state.isLoggedIn = false
    state.isLoading = false
  },
  [logoutRequest] (state) {
    sessionStorage.removeItem('token')
    sessionStorage.removeItem('role')
    state.isLoggedIn = false
  },
  [permissionsRequest] (state) {},
  [permissionsResponse] (state, permissions) {
    state.permissions = permissions
  },
  [changeRole] (state, role) {
    state.data.role = role
    sessionStorage.setItem('role', role)
    window.location.reload()
  },
  [changeEmailRequest] (state) {
    state.isChangingEmail = true
  },
  [changeEmailResponse] (state, user) {
    state.isChangingEmail = false
    state.data = { ...state.data, ...user }
  },
  [changePasswordRequest] (state) {
    state.isChangingPassword = true
  },
  [changePasswordResponse] (state, user) {
    state.isChangingPassword = false
    state.data = { ...state.data, hasToChangePassword: false }
  }
}

// Actions
export const login = 'login'
export const logout = 'logout'
export const fetchPermissions = 'fetchPermissions'
export const changeEmail = 'changeEmail'
export const changePassword = 'changePassword'

const actions = {
  [login]: ({ commit }, { email, password }) => {
    commit(loginRequest, { email })

    return Vue.http
      .post(`${SERVER_URL}/login`, {
        email,
        password
      })
      .then(({ data }) => {
        const { token } = data
        commit(loginSuccess, { token })
        commit(solveAllErrors)
      })
      .catch(({ data, status, statusText }) => {
        commit(loginFail)
      })
  },

  [logout] ({ commit }) {
    return new Promise((resolve, reject) => {
      commit(logoutRequest)
      resolve()
    })
  },

  [fetchPermissions]: ({ commit, dispatch }) => {
    commit(permissionsRequest)
    return Vue.http
      .get(`${SERVER_URL}/permissions`)
      .then(({ data: permissions }) => {
        commit(permissionsResponse, permissions)
      })
  },

  [changeEmail]: ({ commit }, email) => {
    commit(changeEmailRequest)
    return Vue.http
      .post(`${SERVER_URL}/settings/changeEmail`, { newEmail: email })
      .then(({ data: user }) => {
        inform(`Die E-Mail-Adresse wurde erfolgreich geändert`)
        commit(changeEmailResponse, user)
      })
  },

  [changePassword]: ({ commit }, password) => {
    commit(changePasswordRequest)
    return Vue.http
      .post(`${SERVER_URL}/settings/changePassword`, { newPassword: password })
      .then(({ data: user }) => {
        inform(`Das Passwort wurde erfolgreich geändert`)
        commit(changePasswordResponse, user)
        commit(loginSuccess, user)
      })
  }
}

// Getters
const getters = {
  isForcedPasswordChange: state => {
    return state.data.hasToChangePassword
  }
}

export default {
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/user.js